<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import Textarea from 'primevue/textarea'
// useUUID composable is auto-imported by Nuxt
import { dialog as dialogStyle } from '~/utils/primeStyles/dialog.ts'

const commerceStore = useCommerceStore()
const businessStore = useBusinessStore()
const posCheckStore = usePosCheckStore()

const { productStaged, productModal, stageProductInCart, productStagedTotal } = storeToRefs(commerceStore)
const { businessInfo } = storeToRefs(businessStore)
const { hasActiveCheck } = storeToRefs(posCheckStore)

const {
  addProductToCart,
  updateProductInCart,
} = commerceStore

const { t } = useNuxtApp().$i18n

const isRestaurantMode = computed(() => businessInfo.value?.service_type === 'restaurant')
const needsActiveCheck = computed(() => isRestaurantMode.value && !hasActiveCheck.value)

function removeModifier(modifierGroupId, selectionId) {
  if (!productStaged.value?.modifiers) return

  const modifierGroup = productStaged.value.modifiers.find(m => m.group_id === modifierGroupId)
  if (!modifierGroup) return

  if (modifierGroup.multiple && Array.isArray(modifierGroup.selected)) {
    const filtered = modifierGroup.selected.filter(s => s.id !== selectionId)
    // For required modifiers, don't remove if it would leave no selections
    if (!modifierGroup.required || filtered.length > 0) {
      modifierGroup.selected = filtered.length > 0 ? filtered : null
    }
  }
  else if (!modifierGroup.required) {
    // Only allow removing single selections if not required
    modifierGroup.selected = null
  }
}

function incrementQuantity() {
  productStaged.value.quantity++
}

function decreaseQuantity() {
  if (productStaged.value.quantity === 0) {
    return
  }

  productStaged.value.quantity--
}

function addToCart() {
  const { generate } = useUUID()
  const cartUuid = generate()
  if (productStaged.value.cart_uuid === undefined) {
    productStaged.value.cart_uuid = cartUuid
  }

  addProductToCart(productStaged.value)
  productModal.value = false
}

function updateCart() {
  if (productStaged.value) {
    updateProductInCart(productStaged.value)
    productModal.value = false
  }
}

function getLowestPricingOptionPrice(product: any): number {
  const pricingMod = getPricingModifier(product)
  if (!pricingMod?.options?.length) return product.price || 0
  const prices = pricingMod.options.map((o: any) => {
    const data = getOptionData(o)
    return data.price_change || data.price_modifier || 0
  })
  return Math.min(...prices)
}

// Check if product has a pricing modifier
function getPricingModifier(product: any) {
  if (!product?.modifiers || !Array.isArray(product.modifiers)) return null
  return product.modifiers.find((m: any) => m.is_pricing) ?? null
}

// Calculate total based on modifiers
watch(productStaged, (product) => {
  if (product === undefined || product === null) {
    return
  }

  const rawPrice = product.price || product.base_price || 0
  if (!product.quantity) return

  // Determine base price — pricing modifier replaces it
  const pricingMod = getPricingModifier(product)
  let basePrice = rawPrice

  if (pricingMod && pricingMod.selected) {
    const sel = Array.isArray(pricingMod.selected) ? pricingMod.selected[0] : pricingMod.selected
    if (sel) {
      basePrice = sel.price_modifier || sel.price_change || 0
    }
  }

  let total = basePrice * product.quantity

  // Add non-pricing modifier costs
  if (product.modifiers && Array.isArray(product.modifiers)) {
    product.modifiers.forEach((modifier: any) => {
      if (modifier.is_pricing) return // already used as base
      if (modifier.selected) {
        const selections = Array.isArray(modifier.selected) ? modifier.selected : [modifier.selected]
        selections.forEach((selection: any) => {
          const priceChange = selection.price_modifier || selection.price_change || 0
          if (priceChange > 0) {
            total += priceChange * product.quantity
          }
        })
      }
    })
  }

  product.total = total
  if (!product.price) {
    product.price = rawPrice
  }
}, { immediate: true, deep: true })

function selectModifierOption(modifierGroupId, option) {
  if (!productStaged.value?.modifiers) return

  const modifierGroup = productStaged.value.modifiers.find(m => m.group_id === modifierGroupId)
  if (!modifierGroup) return

  // Normalize the option to have consistent price_modifier property
  const normalizedOption = {
    id: option.id,
    name: option.name,
    price_modifier: option.price_change || option.price_modifier || 0,
    quantity: 1,
  }

  if (modifierGroup.multiple) {
    if (!Array.isArray(modifierGroup.selected)) {
      modifierGroup.selected = []
    }
    // Toggle selection for multiple choice
    const existingIndex = modifierGroup.selected.findIndex(s => s.id === option.id)
    if (existingIndex >= 0) {
      modifierGroup.selected.splice(existingIndex, 1)
    }
    else {
      modifierGroup.selected.push(normalizedOption)
    }
  }
  else {
    // Single selection
    modifierGroup.selected = normalizedOption
  }
}

// Helper function to check if a modifier has any selections
function hasSelections(modifier) {
  if (!modifier.selected) return false

  if (Array.isArray(modifier.selected)) {
    return modifier.selected.length > 0
  }

  return true
}

// Helper function to get selections array for display
function getSelections(modifier) {
  if (!modifier.selected) return []

  return Array.isArray(modifier.selected) ? modifier.selected : [modifier.selected]
}

// Helper function to extract option data from reactive wrapper
function getOptionData(option) {
  // Handle reactive wrapper from Vue
  if (option._custom && option._custom.value) {
    return option._custom.value
  }
  // Return as-is if already unwrapped
  return option
}

// Helper function to check if an option is selected
function isOptionSelected(modifier, option) {
  if (!modifier.selected) return false

  const optionData = getOptionData(option)

  if (Array.isArray(modifier.selected)) {
    return modifier.selected.some(s => s.id === optionData.id)
  }

  return modifier.selected.id === optionData.id
}

const headerTitle = computed(() => {
  if (!productStaged.value) {
    return 'Product details'
  }

  const { quantity, name } = productStaged.value

  let title = ''

  if (quantity && name) {
    title = `${quantity} x ${name}`
  }

  return title
})

// Check if all required modifiers are selected
const allRequiredModifiersSelected = computed(() => {
  if (!productStaged.value?.modifiers) return true

  return productStaged.value.modifiers.every((modifier) => {
    if (!modifier.required) return true
    return hasSelections(modifier)
  })
})
</script>

<template>
  <AppDialog
    v-model:visible="productModal"
    modal
    :header="headerTitle"
    :pt="dialogStyle"
    position="top"
    @hide="productStaged = null"
  >
    <div class="w-full">
      <div
        v-if="productStaged"
        id="product"
        class="flex flex-col md:flex-row-reverse"
      >
        <div
          id="product-details"
          class="relative flex-grow md:w-1/2"
        >
          <div class="min-h-24 bg-gray-500" />
          <div
            id="item-photo"
            class="absolute left-1/2 top-12 mr-2 size-24 -translate-x-1/2 overflow-hidden rounded-full border-2 border-gray-700 shadow-lg"
          >
            <img
              :src="getProductFirstImage(productStaged)"
              class="w-24"
              :alt="`product: ${productStaged.name}`"
            >
          </div>
          <div class="pt-8 text-center font-sans">
            <h2 class="mb-1 mt-8 font-normal">
              {{ headerTitle }}
            </h2>
            <div class="font-semibold">
              <template v-if="getPricingModifier(productStaged)">
                <span class="text-blue-600">{{ t('From') }}: {{ usePriceFormatter(getLowestPricingOptionPrice(productStaged)) }}</span>
              </template>
              <template v-else>
                <span>P/U: </span>{{ usePriceFormatter(productStaged.price) }}
              </template>
            </div>
            <!-- Selected Modifiers Display -->
            <div
              v-if="productStaged.modifiers && productStaged.modifiers.some(m => hasSelections(m))"
              class="mx-2 my-4"
            >
              <ul>
                <template
                  v-for="modifier in productStaged.modifiers"
                  :key="modifier.group_id"
                >
                  <template v-if="hasSelections(modifier)">
                    <li
                      v-for="selection in getSelections(modifier)"
                      :key="selection.id"
                      class="mb-1 flex items-center justify-center"
                    >
                      <span class="flex w-full items-center justify-between">
                        <span class="mx-1">{{ selection.name }}</span>
                        <span class="mx-3 text-sm">
                          <span
                            v-if="(selection.price_modifier || selection.price_change) > 0"
                            class="font-medium"
                            :class="modifier.is_pricing ? 'text-blue-600' : 'text-green-600'"
                          >
                            {{ modifier.is_pricing ? '' : '+' }}{{ usePriceFormatter(selection.price_modifier || selection.price_change) }}
                          </span>
                          <span
                            v-else
                            class="text-gray-500"
                          >
                            {{ usePriceFormatter(0) }}
                          </span>
                        </span>
                      </span>
                      <Button
                        v-if="modifier.multiple && (!modifier.required || getSelections(modifier).length > 1)"
                        class="remove-button p-button-danger rounded-full p-2"
                        aria-label="Remove modifier"
                        @click="removeModifier(modifier.group_id, selection.id)"
                      >
                        <i class="pi pi-trash" />
                      </Button>
                    </li>
                  </template>
                </template>
              </ul>
            </div>

            <!-- Special Instructions -->
            <div class="mb-4 p-4">
              <label class="text-sm font-medium text-gray-700 mb-1 block">
                {{ $t('Special instructions') }}
              </label>
              <Textarea
                v-model="productStaged.notes"
                :placeholder="$t('Add any special requests or instructions')"
                rows="3"
                class="w-full"
              />
            </div>

            <div class="mb-5 text-2xl font-bold">
              Total: {{ usePriceFormatter(productStagedTotal) }}
            </div>
            <div class="flex bg-stone-200 py-2">
              <div class="flex w-1/2 justify-center">
                <Button
                  class="rounded-full"
                  icon="pi pi-minus"
                  aria-lable="Remove one"
                  severity="warning"
                  @click="decreaseQuantity"
                />
                <div v-if="productStaged?.quantity">
                  <span class="mx-4 font-serif text-3xl">{{ productStaged.quantity }}</span>
                </div>
                <Button
                  class="rounded-full"
                  icon="pi pi-plus"
                  aria-lable="Add one"
                  severity="success"
                  @click="incrementQuantity"
                />
              </div>
              <div class="w-1/2 text-center">
                <Button
                  v-if="stageProductInCart"
                  :label="$t('Update')"
                  icon="pi pi-pencil"
                  class="rounded-full border-blue-950 bg-blue-800 p-2 text-sm hover:bg-blue-600"
                  @click="updateCart"
                />
                <div v-else>
                  <Button
                    :label="$t('Add to cart')"
                    icon="pi pi-check"
                    class="rounded-full border-red-950 text-white bg-red-800 p-2 text-sm hover:bg-red-600"
                    :disabled="productStaged.quantity === 0 || !allRequiredModifiersSelected || needsActiveCheck"
                    autofocus
                    @click="addToCart"
                  />
                  <div
                    v-if="needsActiveCheck"
                    class="text-xs text-red-600 mt-1"
                  >
                    {{ t('Select table or client first') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Unified Modifier System -->
        <div
          v-if="productStaged.modifiers && productStaged.modifiers.length > 0"
          class="flex flex-col gap-2 md:w-1/2"
        >
          <div
            v-for="modifier in productStaged.modifiers"
            :key="modifier.group_id"
            class="rounded-md bg-white p-3"
          >
            <h3 class="mb-2 mt-0 text-base font-medium">
              {{ modifier.group_name }}
              <span
                v-if="modifier.required"
                class="text-red-500"
              >*</span>
            </h3>
            <div class="mb-0 grid grid-cols-2 gap-2">
              <div
                v-for="option in modifier.options"
                :key="option.id"
              >
                <Button
                  class="w-full text-base"
                  :class="{
                    'bg-gray-500': isOptionSelected(modifier, option),
                  }"
                  @click="selectModifierOption(modifier.group_id, getOptionData(option))"
                >
                  <span class="flex w-full items-center justify-between">
                    <span>{{ getOptionData(option).name }}</span>
                    <span
                      v-if="getOptionData(option).price_change > 0"
                      class="text-base"
                    >{{ modifier.is_pricing ? '' : '+' }}{{ usePriceFormatter(getOptionData(option).price_change) }}</span>
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppDialog>
</template>
